import React, { useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  Divider,
  TextField,
  Button,
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SendIcon from '@mui/icons-material/Send';
import { Link as RouterLink } from 'react-router-dom';

const linkStyle = {
  color: '#BBBBBB',
  textDecoration: 'none',
  fontSize: '0.85rem',
  transition: 'all 0.2s',
  '&:hover': { color: '#D4AF37', pl: 0.5 },
};

const Footer = () => {
  const [email, setEmail] = useState('');

  return (
    <Box component="footer" sx={{ mt: 8 }}>
      {/* Newsletter Banner */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1B1464 0%, #2D1B69 100%)',
          py: { xs: 4, md: 5 },
          px: 2,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: 'white', mb: 1, fontFamily: "'Playfair Display', serif", fontSize: { xs: '1.3rem', md: '2rem' } }}
            >
              Stay Connected
            </Typography>
            <Typography variant="body2" sx={{ color: '#CCCCCC', mb: 3 }}>
              Subscribe to get updates on new arrivals, exclusive offers & silver rate alerts
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                maxWidth: 480,
                mx: 'auto',
                gap: 1,
              }}
            >
              <TextField
                fullWidth
                size="small"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                InputProps={{
                  sx: {
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    borderRadius: '25px',
                    color: 'white',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                    '&:hover fieldset': { borderColor: '#D4AF37' },
                    '&.Mui-focused fieldset': { borderColor: '#D4AF37' },
                    fontSize: '0.85rem',
                  },
                }}
              />
              <Button
                variant="contained"
                sx={{
                  backgroundColor: '#D4AF37',
                  color: '#1A1A2E',
                  borderRadius: '25px',
                  px: 3,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  '&:hover': { backgroundColor: '#E8D48B' },
                }}
                endIcon={<SendIcon />}
              >
                Subscribe
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Main Footer */}
      <Box sx={{ backgroundColor: '#0A0825', color: 'white', pt: { xs: 4, md: 6 }, pb: 3 }}>
        <Container maxWidth="xl">
          <Grid container spacing={4}>
            {/* Brand */}
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box
                  component="img"
                  src="/logo.jpeg"
                  alt="Valli Velli Maligai Logo"
                  sx={{ height: 50, width: 'auto', borderRadius: '50%', border: '2px solid #D4AF37' }}
                />
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: '#D4AF37', lineHeight: 1.1, fontFamily: "'Playfair Display', serif" }}>
                    VALLI VELLI
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#E8D48B', letterSpacing: 2, fontWeight: 600 }}>
                    MALIGAI
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" sx={{ mb: 2, color: '#999', lineHeight: 1.7, fontSize: '0.8rem' }}>
                Your trusted destination for premium BIS hallmarked silver jewelry.
                Where tradition meets silver sophistication since generations.
              </Typography>
            </Grid>

            {/* Quick Links */}
            <Grid item xs={6} sm={4} md={2}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: '#D4AF37', letterSpacing: 1, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                Quick Links
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                <Link component={RouterLink} to="/" sx={linkStyle}>Home</Link>
                <Link component={RouterLink} to="/store" sx={linkStyle}>Store</Link>
                <Link component={RouterLink} to="/contact" sx={linkStyle}>Contact Us</Link>
                <Link href="#" sx={linkStyle}>About Us</Link>
                <Link href="#" sx={linkStyle}>Track Order</Link>
              </Box>
            </Grid>

            {/* Categories */}
            <Grid item xs={6} sm={4} md={2}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: '#D4AF37', letterSpacing: 1, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                Categories
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                <Link component={RouterLink} to="/store/necklaces" sx={linkStyle}>Necklaces</Link>
                <Link component={RouterLink} to="/store/earrings" sx={linkStyle}>Earrings</Link>
                <Link component={RouterLink} to="/store/rings" sx={linkStyle}>Rings</Link>
                <Link component={RouterLink} to="/store/bangles" sx={linkStyle}>Bangles</Link>
                <Link component={RouterLink} to="/store/coins-bars" sx={linkStyle}>Coins & Bars</Link>
                <Link component={RouterLink} to="/store/pendants" sx={linkStyle}>Pendants</Link>
              </Box>
            </Grid>

            {/* Store Locations */}
            <Grid item xs={12} sm={4} md={2}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: '#D4AF37', letterSpacing: 1, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                Our Stores
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {[
                  { name: 'Vengamedu, Karur', phone: '+91 6382939365' },
                  { name: 'Paramathi Velur', phone: '+91 9944683233' },
                  { name: 'Namakkal', phone: '+91 9894058033' },
                ].map((store, i) => (
                  <Box key={i}>
                    <Typography variant="body2" sx={{ color: '#CCC', fontWeight: 600, fontSize: '0.8rem' }}>
                      {store.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#888' }}>
                      {store.phone}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>

            {/* Contact Info */}
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: '#D4AF37', letterSpacing: 1, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                Get In Touch
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneIcon sx={{ color: '#D4AF37', fontSize: 18 }} />
                  <Typography variant="body2" sx={{ color: '#BBB', fontSize: '0.85rem' }}>
                    +91 6382939365
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon sx={{ color: '#D4AF37', fontSize: 18 }} />
                  <Typography variant="body2" sx={{ color: '#BBB', fontSize: '0.85rem' }}>
                    info@vallivelli.com
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <LocationOnIcon sx={{ color: '#D4AF37', fontSize: 18, mt: 0.3 }} />
                  <Typography variant="body2" sx={{ color: '#BBB', fontSize: '0.85rem', lineHeight: 1.6 }}>
                    Vengamedu, Karur District,
                    <br />Tamil Nadu, India
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mt: 2, p: 2, backgroundColor: 'rgba(212,175,55,0.08)', borderRadius: 2, border: '1px solid rgba(212,175,55,0.15)' }}>
                <Typography variant="caption" sx={{ color: '#D4AF37', fontWeight: 600 }}>
                  🕐 Store Hours
                </Typography>
                <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 0.5 }}>
                  Mon – Sat: 9:00 AM – 8:00 PM
                  <br />Sunday: 10:00 AM – 6:00 PM
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3, backgroundColor: '#1B1464' }} />

          {/* Bottom Bar */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            <Typography variant="caption" sx={{ color: '#777' }}>
              © {new Date().getFullYear()} VALLI VELLI MALIGAI. All rights reserved.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Link component={RouterLink} to="/privacy-policy" sx={{ color: '#777', textDecoration: 'none', fontSize: '0.75rem', '&:hover': { color: '#D4AF37' } }}>
                Privacy Policy
              </Link>
              <Link component={RouterLink} to="/terms-and-conditions" sx={{ color: '#777', textDecoration: 'none', fontSize: '0.75rem', '&:hover': { color: '#D4AF37' } }}>
                Terms & Conditions
              </Link>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Footer;
